interface ExtendedDisplayMediaStreamOptions extends DisplayMediaStreamOptions {
  preferCurrentTab?: boolean;
  selfBrowserSurface?: 'include' | 'exclude';
  surfaceSwitching?: 'include' | 'exclude';
  monitorTypeSurfaces?: 'include' | 'exclude';
}

export const captureFeedbackScreenshot = async (): Promise<File> => {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error('Screenshot capture is not supported in this browser.');
  }

  let stream: MediaStream | null = null;

  try {
    const displayMediaOptions: ExtendedDisplayMediaStreamOptions = {
      video: {
        displaySurface: 'browser',
        frameRate: 1,
      },
      audio: false,
      // Browser-specific hints: prefer this tab and hide other surface categories when supported.
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
      monitorTypeSurfaces: 'exclude',
    };

    stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);

    const track = stream.getVideoTracks()[0];
    if (!track) {
      throw new Error('No video stream available for screenshot capture.');
    }

    const selectedSurface = track.getSettings().displaySurface;
    if (selectedSurface && selectedSurface !== 'browser') {
      throw new Error('Please select the current browser tab to attach a screenshot.');
    }

    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Unable to capture screenshot preview.');
    }

    context.drawImage(video, 0, 0, width, height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result) {
          resolve(result);
          return;
        }
        reject(new Error('Unable to convert screenshot to image file.'));
      }, 'image/png');
    });

    return new File([blob], `feedback-capture-${Date.now()}.png`, { type: 'image/png' });
  } finally {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  }
};